import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { getMapStyleJS } from '@/utilities/mapStyleUtils'

import { updateDataConfig, setMapStyle } from '@/actions'
import { apis } from '@/config/apiConfig'
import { DRAW } from '@/data/Layers/Auxiliary/Draw'
import { MEASURE } from '@/data/Layers/Auxiliary/Measure'
import { MEASURE_CONTROL } from '@/data/Layers/Auxiliary/MeasureControl'
import { DELETE } from '@/data/Layers/Auxiliary/Delete'
import { GEOCODE, COORDINATE_SEARCH } from '@/data/Layers/Auxiliary/Search'
import { CLICKED_FEATURE } from '@/data/Layers/Auxiliary/ClickedFeature'
import { SELECTED_FEATURES } from '@/data/Layers/Auxiliary/SelectedFeatures'
import { GEO_SPATIAL_RESULTS } from '@/data/Layers/Auxiliary/GeoSpatialResults'
import { apiFetch } from '@/utilities/api'
import {
  buildUserLayersArray,
  getImageryLayers,
} from '@/utilities/dataConfigSelectors'
import {
  addLayerToStyle,
  addSourceToStyle,
  resetMapStyle,
} from '@/utilities/mapStyle'

import { useIsFeatureEnabled } from '@/contexts/AnalyticsProvider'
import { useUser } from '@/queries/hooks/use-user'

// Type definitions
interface User {
  mapID: string
  accountID: string
  [key: string]: any
}

interface TOC {
  id?: string
  type?: string
  userLayer?: boolean
}

interface LayerChildren {
  groupIds: string[]
  layerIds: string[]
  order?: string[]
  items?: Layer[]
}

interface Layer {
  id?: string
  groupId?: string
  toc?: TOC
  children?: LayerChildren
  layersArray?: any[]
  sourcesArray?: any[]
  [key: string]: any
}

interface DataConfig {
  tocLayers: Layer[]
  commonLayers: Record<string, Layer>
  auxiliaryLayers: Layer[]
  imageryLayers: Layer[]
  layerHierarchy: any
}

interface LoadDataConfigParams {
  /** The ID of the layer to be deleted from the map style */
  layerDeletedId?: string
  /** Whether to update the map style after loading data config (default: true) */
  updateStyle?: boolean
  /** Whether to reset the map style completely (default: false) */
  resetStyle?: boolean
  /** Whether to refetch user data before loading data config (default: false) */
  refetchUser?: boolean
}

interface LoadDataConfigResult {
  dataConfig: DataConfig
  user: User
}

interface DataConfigContextValue {
  isLoading: boolean
  error: Error | null
  loadDataConfig: (
    params?: LoadDataConfigParams
  ) => Promise<LoadDataConfigResult>
}

interface DataConfigProviderProps {
  children: ReactNode
}

interface RootState {
  mapStyle: any
}

/**
 * Auto-group individual USLandGrid layers by state for the TOC.
 *
 * When users have individual `uslandgrid_parcels_*` IDs in their saved layers
 * (rather than the `group_uslandgrid_*` group ID), `buildUserLayersArray`
 * returns them flat. This function wraps those orphan layers into their parent
 * state group so the TOC always renders them nested under "US Land Grid: TX".
 */
const autoGroupUSLandGridLayers = (
  userLayers: Layer[],
  masterCommonLayerList: Record<string, Layer>
): Layer[] => {
  // Build a reverse map: individual layer toc.id → group object in masterCommonLayerList
  const layerIdToGroup: Record<string, Layer> = {}
  Object.values(masterCommonLayerList).forEach((item: Layer) => {
    if (
      typeof item.id === 'string' &&
      item.id.startsWith('group_uslandgrid_') &&
      item.children?.layerIds
    ) {
      item.children.layerIds.forEach((layerId: string) => {
        layerIdToGroup[layerId] = item
      })
    }
  })

  // No ArcGIS groups available — nothing to auto-group
  if (Object.keys(layerIdToGroup).length === 0) return userLayers

  // Separate top-level USLandGrid layers from everything else
  const otherLayers: Layer[] = []
  const uslandgridByGroup: Record<string, Layer[]> = {}
  const existingGroupIds = new Set<string>()

  userLayers.forEach((layer: Layer) => {
    // Track groups already in the user's layer list (from prior activation)
    if (
      typeof layer.id === 'string' &&
      layer.id.startsWith('group_uslandgrid_')
    ) {
      existingGroupIds.add(layer.id)
      otherLayers.push(layer)
      return
    }

    const tocId = layer.toc?.id
    if (
      typeof tocId === 'string' &&
      tocId.startsWith('uslandgrid_') &&
      layerIdToGroup[tocId]
    ) {
      const group = layerIdToGroup[tocId]
      const groupId = group.id as string
      // Skip if this group was already in the user's saved layers
      // (its children are already nested by processUserLayers)
      if (existingGroupIds.has(groupId)) {
        otherLayers.push(layer)
        return
      }
      if (!uslandgridByGroup[groupId]) uslandgridByGroup[groupId] = []
      uslandgridByGroup[groupId].push(layer)
    } else {
      otherLayers.push(layer)
    }
  })

  // No orphan USLandGrid layers to group
  if (Object.keys(uslandgridByGroup).length === 0) return userLayers

  // Create group wrappers with children.items (the format the TOC renderer expects)
  const groupWrappers: Layer[] = Object.entries(uslandgridByGroup).map(
    ([groupId, layers]) => {
      const groupTemplate = masterCommonLayerList[groupId]
      // Assign draggableIndex to each child
      layers.forEach((child, idx) => {
        if (child.toc) {
          child.toc = { ...child.toc, draggableIndex: idx + 1 } as any
        }
      })
      return {
        ...groupTemplate,
        children: {
          ...(groupTemplate.children as LayerChildren),
          items: layers,
        },
      }
    }
  )

  return [...otherLayers, ...groupWrappers]
}

const removeLayerFromStyle = (mapStyle: any, layerDeletedId: string): any => {
  const style = getMapStyleJS(mapStyle)
  if (!style) return mapStyle
  // Deep clone to avoid mutating
  const clonedStyle = JSON.parse(JSON.stringify(style))
  clonedStyle.layers.forEach((layer: any) => {
    if (layer.id === layerDeletedId) {
      layer.layout.visibility = 'none'
    }
    if (layer.id === `${layerDeletedId}_outline`) {
      layer.layout.visibility = 'none'
    }
    if (layer.id === `${layerDeletedId}_symbol`) {
      layer.layout.visibility = 'none'
    }
  })
  return clonedStyle
}

const addLayersToStyle = (mapStyle: any, dataConfig: DataConfig): any => {
  let style = getMapStyleJS(mapStyle)
  if (!style) return mapStyle
  // Deep clone to avoid mutating
  style = JSON.parse(JSON.stringify(style))
  const checkStyleForLayer = (layerId: string): boolean => {
    const check = style.layers.filter((layer: any) => layer.id === layerId)
    return check.length > 0
  }

  const processTocLayer = (layerItem: Layer): void => {
    if (layerItem.layersArray) {
      layerItem.layersArray.forEach(layer => {
        const inStyle = checkStyleForLayer(layer.layer.id)
        if (!inStyle) {
          layer.beforeLayer = 'country_label-other'
          style = addLayerToStyle(style, layer)
        }
      })
      layerItem.sourcesArray?.forEach(source => {
        if (!style.sources[source.id]) {
          style = addSourceToStyle(style, source)
        }
      })
    } else if (layerItem.children?.items) {
      layerItem.children.items.forEach(childLayer => {
        processTocLayer(childLayer)
      })
    }
  }

  dataConfig.tocLayers.forEach(tocLayer => {
    processTocLayer(tocLayer)
  })

  return style
}

const DataConfigContext = createContext<DataConfigContextValue | undefined>(
  undefined
)

export const DataConfigProvider: React.FC<DataConfigProviderProps> = ({
  children,
}) => {
  const dispatch = useDispatch()
  const store = useStore<RootState>()
  const { data: user, refetch: refetchUser } = useUser()
  const mapStyle = useSelector((state: RootState) => state.mapStyle)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  /**
   * Loads the data configuration for the map, including user layers, common layers, and auxiliary layers.
   *
   * @param params - Configuration parameters for loading data
   * @param params.layerDeletedId - The ID of the layer to be deleted from the map style
   * @param params.updateStyle - Whether to update the map style after loading data config (default: true)
   * @param params.resetStyle - Whether to reset the map style completely (default: false)
   * @param params.refetchUser - Whether to refetch user data before loading data config (default: false)
   * @returns Promise that resolves to an object containing the loaded dataConfig and user data
   */
  const loadDataConfig = useCallback(
    async ({
      layerDeletedId,
      updateStyle = true,
      resetStyle = false,
      refetchUser: refetchUserParam = false,
    }: LoadDataConfigParams = {}): Promise<LoadDataConfigResult> => {
      setIsLoading(true)
      setError(null)

      // Use fresh user data if explicitly requested or missing critical data
      const shouldRefetchUser =
        refetchUserParam || !user?.isLoggedIn || !user?.mapID
      const dataConfigUser = shouldRefetchUser
        ? (await refetchUser()).data
        : user

      return new Promise((resolve, reject) => {
        Promise.resolve().then(async () => {
          try {
            // First API call to get master common layer list
            const masterCommonLayerListPromise = new Promise<any>(
              (res, rej) => {
                const url = `${apis.apiDatabase.uri}layers/common?mapID=${dataConfigUser.mapID}&nested=true`
                apiFetch(url, 'GET', null, result => {
                  if (result) res(result)
                  else rej(new Error('Failed to fetch common layers'))
                })
              }
            )

            // Second API call to get user layers
            const userLayersResultPromise = new Promise<any>((res, rej) => {
              const url = `${apis.apiDatabase.uri}layers/user?mapID=${dataConfigUser.mapID}`
              apiFetch(url, 'GET', null, result => {
                if (result) res(result)
                else rej(new Error('Failed to fetch user layers'))
              })
            })

            // Third API call to get ArcGIS VectorTileServer common-layer configs
            const arcgisLayersResultPromise = new Promise<any>((res, rej) => {
              const url = `${apis.apiDatabase.uri}arcgis/layers?mapID=${dataConfigUser.mapID}`
              apiFetch(url, 'GET', null, result => {
                if (result) res(result)
                else rej(new Error('Failed to fetch ArcGIS layers'))
              })
            })

            const [commonResult, userLayersResult, arcgisLayersResult] =
              await Promise.allSettled([
                masterCommonLayerListPromise,
                userLayersResultPromise,
                arcgisLayersResultPromise,
              ])

            if (commonResult.status === 'rejected') {
              throw commonResult.reason
            }

            if (userLayersResult.status === 'rejected') {
              throw userLayersResult.reason
            }

            if (arcgisLayersResult.status === 'rejected') {
              console.warn(
                'ArcGIS layer fetch failed:',
                arcgisLayersResult.reason
              )
            }

            const resolvedCommonResult = commonResult.value ?? {}
            const resolvedUserLayersResult = userLayersResult.value ?? {}
            const resolvedArcGISLayersResult =
              arcgisLayersResult.status === 'fulfilled'
                ? arcgisLayersResult.value
                : null

            // Transform the backend's array-based children structure to the frontend's expected format
            let masterCommonLayerList: Record<string, Layer> = {}
            if (Array.isArray(resolvedCommonResult?.data)) {
              resolvedCommonResult.data.forEach((layer: any) => {
                if (layer.id) {
                  // Transform children structure if it's an array (from backend) to the frontend's expected format
                  if (Array.isArray(layer.children)) {
                    const childrenArray = layer.children
                    layer.children = {
                      order: [],
                      groupIds: [],
                      layerIds: [],
                    }
                    childrenArray.forEach((child: any) => {
                      if (child.id) {
                        layer.children.order.push(child.id)
                        if (child.id.startsWith('group_common_')) {
                          layer.children.groupIds.push(child.id)
                        } else {
                          layer.children.layerIds.push(child.id)
                        }
                        // Also add the child to the masterCommonLayerList
                        masterCommonLayerList[child.id] = child
                      }
                    })
                  }
                  masterCommonLayerList[layer.id] = layer
                }
              })
            } else {
              masterCommonLayerList = resolvedCommonResult ?? {}
            }

            // Backwards-compatible: handle both flat array (old) and { layers, groups } (new)
            const arcgisData = resolvedArcGISLayersResult?.data
            const arcgisLayerConfigs: Layer[] = Array.isArray(arcgisData)
              ? arcgisData
              : (arcgisData?.layers ?? [])
            const arcgisGroups: any[] = Array.isArray(arcgisData)
              ? []
              : (arcgisData?.groups ?? [])

            // Add individual layers to masterCommonLayerList
            arcgisLayerConfigs.forEach((layerConfig: Layer) => {
              const layerId = layerConfig?.toc?.id
              if (layerId) {
                masterCommonLayerList[layerId] = layerConfig
              }
            })

            // Process ArcGIS state groups — transform children arrays to frontend format
            arcgisGroups.forEach((group: any) => {
              if (group.id && Array.isArray(group.children)) {
                const childrenArray = group.children
                group.children = {
                  order: [] as string[],
                  groupIds: [] as string[],
                  layerIds: [] as string[],
                }
                childrenArray.forEach((child: any) => {
                  const childId = child.id ?? child.toc?.id
                  if (childId) {
                    group.children.order.push(childId)
                    if (
                      typeof childId === 'string' &&
                      childId.startsWith('group_uslandgrid_')
                    ) {
                      group.children.groupIds.push(childId)
                    } else {
                      group.children.layerIds.push(childId)
                    }
                  }
                })
                masterCommonLayerList[group.id] = group
              }
            })

            let userLayers: Layer[] = []
            if (resolvedUserLayersResult.data?.user && masterCommonLayerList) {
              userLayers = buildUserLayersArray(
                resolvedUserLayersResult.data.user,
                masterCommonLayerList
              )

              // Auto-group individual USLandGrid layers by state.
              // Users may have individual layer IDs saved (not the group ID).
              // This wraps those orphan layers into their parent state group
              // so the TOC always renders them grouped.
              userLayers = autoGroupUSLandGridLayers(
                userLayers,
                masterCommonLayerList
              )
            }

            const imageryLayers = getImageryLayers(dataConfigUser)
            const auxiliaryLayers: Layer[] = [
              SELECTED_FEATURES,
              GEO_SPATIAL_RESULTS,
              CLICKED_FEATURE,
              DRAW,
              DELETE,
              MEASURE,
              MEASURE_CONTROL,
              COORDINATE_SEARCH,
              GEOCODE,
            ]

            const dataConfig: DataConfig = {
              tocLayers: [...userLayers],
              commonLayers: masterCommonLayerList,
              auxiliaryLayers,
              imageryLayers,
              layerHierarchy:
                resolvedUserLayersResult.data?.layerHierarchy ?? null,
            }

            if (updateStyle) {
              // Read mapStyle fresh (we're past an await — the render-closure
              // value could be stale) and set the full rebuilt style.
              const currentMapStyle = store.getState().mapStyle
              if (layerDeletedId) {
                const newMapStyle = removeLayerFromStyle(
                  currentMapStyle,
                  layerDeletedId
                )
                dispatch(setMapStyle(newMapStyle))
              } else {
                const newMapStyle = addLayersToStyle(
                  currentMapStyle,
                  dataConfig
                )
                dispatch(setMapStyle(newMapStyle))
              }
            }
            if (resetStyle) {
              const newMapStyle = resetMapStyle(dataConfig)
              dispatch(setMapStyle(newMapStyle))
            }

            dispatch(updateDataConfig(dataConfig))
            const result: LoadDataConfigResult = {
              dataConfig,
              user: dataConfigUser,
            }
            // returns dataConfig and user to the caller
            resolve(result)
            setIsLoading(false)
          } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err))
            setError(error)
            setIsLoading(false)
            reject(error)
          }
        })
      })
    },
    [dispatch, mapStyle, refetchUser, user]
  )

  const value: DataConfigContextValue = {
    isLoading,
    error,
    loadDataConfig,
  }

  return (
    <DataConfigContext.Provider value={value}>
      {children}
    </DataConfigContext.Provider>
  )
}

export const useDataConfig = (): DataConfigContextValue => {
  const context = useContext(DataConfigContext)
  if (context === undefined) {
    throw new Error('useDataConfig must be used within a DataConfigProvider')
  }
  return context
}
